import { createContext, useContext, useEffect } from 'react'
import { ThemeProvider } from '@mui/material/styles'

import { useBranding } from '../hooks'
import { createBrandingTheme } from '../mui.config'

export const BrandingContext = createContext(null)

export const useBrandingContext = () => useContext(BrandingContext)

// List of available Google Fonts that can be selected in Instance Branding
const GOOGLE_FONTS = [
  'Roboto',
  'Open Sans',
  'Lato',
  'Montserrat',
  'Poppins',
  'Raleway',
  'Inter',
  'Playfair Display',
  'Merriweather',
  'Nunito',
  'Work Sans',
  'Quicksand',
  'Josefin Sans',
  'Bebas Neue',
  'Oswald',
]

function loadGoogleFont(fontFamily) {
  if (!fontFamily || !GOOGLE_FONTS.includes(fontFamily)) {
    return
  }

  // Check if font is already loaded
  const existingLink = document.querySelector(`link[data-font="${fontFamily}"]`)
  if (existingLink) {
    return
  }

  // Remove other font links to avoid loading unnecessary fonts
  document.querySelectorAll('link[data-font]').forEach((link) => {
    if (link.dataset.font !== fontFamily) {
      link.remove()
    }
  })

  // Create and add the font link
  const link = document.createElement('link')
  link.href = `https://fonts.googleapis.com/css2?family=${fontFamily.replace(
    ' ',
    '+'
  )}:wght@300;400;500;600;700&display=swap`
  link.rel = 'stylesheet'
  link.dataset.font = fontFamily
  document.head.appendChild(link)
}

export function BrandingProvider({ children }) {
  const branding = useBranding()

  // Always provide a theme, even if branding is not loaded yet
  const theme = createBrandingTheme(branding)

  // Load the Google Font when branding changes
  useEffect(() => {
    const fontFamily = branding?.fontFamily || branding?.theme?.typography?.fontFamily || 'Montserrat'

    loadGoogleFont(fontFamily)

    // Force apply the font to body element and all MUI elements
    document.body.style.fontFamily = `${fontFamily}, "Helvetica Neue", Arial, sans-serif`

    // Also force update all MUI Typography elements
    const style = document.createElement('style')
    style.innerHTML = `
      body,
      .MuiTypography-root,
      .MuiButton-root,
      .MuiInput-root,
      .MuiFormLabel-root,
      .MuiTableCell-root,
      .MuiTab-root,
      .MuiChip-root,
      .MuiAlert-root,
      .MuiDialogTitle-root,
      .MuiDialogContent-root,
      .MuiDialogContentText-root,
      [class*="MuiTypography-"] {
        font-family: ${fontFamily}, "Helvetica Neue", Arial, sans-serif !important;
      }
    `
    style.id = 'branding-font-override'

    // Remove any existing style
    const existingStyle = document.getElementById('branding-font-override')
    if (existingStyle) {
      existingStyle.remove()
    }

    document.head.appendChild(style)
  }, [branding?.fontFamily, branding?.theme?.typography?.fontFamily])

  return (
    <BrandingContext.Provider value={branding}>
      <ThemeProvider theme={theme}>{children}</ThemeProvider>
    </BrandingContext.Provider>
  )
}
